/**
 * Sound utility functions for playing audio effects
 */

// Cache for preloaded audio elements
const audioCache = new Map();

// Throttle mechanism to prevent rapid sound playing
let lastPlayTime = 0;
const PLAY_THROTTLE_MS = 100; // Minimum 100ms between sounds

/**
 * Play a like sound effect
 * @param {string} soundType - Type of sound ('like' or 'unlike')
 */
export function playLikeSound(soundType = 'like') {
    try {
        // Check if audio is supported
        if (typeof Audio === 'undefined') {
            console.warn('Audio not supported in this environment');
            return;
        }

        // Throttle rapid clicks to prevent audio overlap
        const now = Date.now();
        if (now - lastPlayTime < PLAY_THROTTLE_MS) {
            return;
        }
        lastPlayTime = now;

        const soundFile = soundType === 'unlike' ? 'unlike.mp3' : 'like.mp3';

        // Use cached audio if available, otherwise create new one
        let audio = audioCache.get(soundFile);

        if (!audio) {
            audio = new Audio();
            audio.src = require(`@/assets/sounds/${soundFile}`);
            audio.volume = 0.3; // 30% volume
            audio.preload = 'auto';
            audioCache.set(soundFile, audio);
        }

        // Reset audio to beginning and play
        audio.currentTime = 0;
        audio.play().catch(error => {
            console.warn('Could not play like sound:', error);
        });

    } catch (error) {
        console.warn('Error playing like sound:', error);
    }
}

/**
 * Preload sound files for better performance
 */
export function preloadSounds() {
    try {
        const sounds = ['like.mp3', 'unlike.mp3'];

        sounds.forEach(soundFile => {
            // Only create if not already cached
            if (!audioCache.has(soundFile)) {
                const audio = new Audio();
                audio.src = require(`@/assets/sounds/${soundFile}`);
                audio.volume = 0.3;
                audio.preload = 'auto';
                audioCache.set(soundFile, audio);
            }
        });
    } catch (error) {
        console.warn('Error preloading sounds:', error);
    }
}

/**
 * Check if sounds are enabled (can be extended with user preferences)
 * @returns {boolean}
 */
export function areSoundsEnabled() {
    // Check if audio is supported and user hasn't disabled sounds
    return typeof Audio !== 'undefined';
}

/**
 * Clear the audio cache (useful for memory management)
 */
export function clearAudioCache() {
    audioCache.clear();
}

/**
 * Get cache size for debugging
 * @returns {number}
 */
export function getCacheSize() {
    return audioCache.size;
}
